import { Component } from '@theme/component';
import { debounce } from '@theme/utilities';

const ANIMATION_OPTIONS = {
  duration: 500,
};

/**
 * A custom element that displays a marquee.
 *
 * @typedef {object} Refs
 * @property {HTMLElement} wrapper - The wrapper element.
 * @property {HTMLElement} content - The content element.
 *
 * @extends Component<Refs>
 */
class MarqueeComponent extends Component {
  requiredRefs = ['wrapper', 'content'];

  connectedCallback() {
    super.connectedCallback();

    const { content } = this.refs;
    if (content.firstElementChild?.children.length === 0) return;

    this.#dpr = window.devicePixelRatio || 1;
    this.#jsRaf = null;
    this.#jsX = 0;
    this.#jsLast = 0;

    // Direction: 'normal' moves LEFT; 'reverse' moves RIGHT
    const dir = this.dataset.movementDirection;
    this.#directionSign = (dir === 'reverse') ? 1 : -1;

    this.#addRepeatedItems();
    this.#duplicateContent();

    // 🔧 Wait for fonts/layout to stabilise before measuring widths
    this.#settleLayout(() => {
      // Set px-based distance + CSS duration for desktop
      this.#syncDesktopAnimation();

      // 🔑 Derive JS (mobile) speed from the same CSS duration so both match
      {
        const distPx = this.#loopWidth();
        const cssDur = parseFloat(getComputedStyle(this.refs.wrapper).getPropertyValue('--marquee-speed')) || (distPx / 120);
        this.#jsPxPerSec = distPx / cssDur;
      }

      // Keep your existing CSS speed var too (for desktop path)
      this.#setSpeed();

      // Choose mode (CSS desktop vs JS mobile)
      this.#setMode();

      // Mark as ready so CSS can reveal the component (you added the CSS in Part 1)
      this.setAttribute('data-ready', '');
    });

    window.addEventListener('resize', this.#handleResize);
    this.addEventListener('pointerenter', this.#slowDown);
    this.addEventListener('pointerleave', this.#speedUp);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    window.removeEventListener('resize', this.#handleResize);
    this.removeEventListener('pointerenter', this.#slowDown);
    this.removeEventListener('pointerleave', this.#speedUp);
    this.#stopJsLoop();
  }

  /**
   * Public: recompute widths/speed & reset (used by onload lock below)
   */
  recomputeAll() {
    this.#stopJsLoop();
    this.#cleanupClonedContent();
    this.#addRepeatedItems();
    this.#duplicateContent();

    this.#settleLayout(() => {
      this.#syncDesktopAnimation();

      // Re-sync JS speed with desktop after any recompute
      {
        const distPx = this.#loopWidth();
        const cssDur = parseFloat(getComputedStyle(this.refs.wrapper).getPropertyValue('--marquee-speed')) || (distPx / 120);
        this.#jsPxPerSec = distPx / cssDur;
      }

      this.#setSpeed();
      this.#restartAnimation(); // CSS path
      this.#setMode();          // re-pick mode after changes
      this.setAttribute('data-ready', '');
    });
  }

  // ====== State & helpers ======
  /** @type {{ cancel: () => void, current: number } | null} */
  #animation = null;
  #jsRaf = null;
  #jsX = 0;
  #jsLast = 0;
  #jsPxPerSec = 120;   // runtime value; synced from CSS duration
  #dpr = 1;
  #directionSign = -1; // left by default

  get clonedContent() {
    const { content, wrapper } = this.refs;
    const lastChild = wrapper.lastElementChild;
    return content !== lastChild ? lastChild : null;
  }

  // Wait for fonts + a couple of frames so widths are stable before we measure
  #settleLayout(cb) {
    const proceed = () => {
      let last = 0, checks = 0;
      const check = () => {
        const w = this.#loopWidth();
        // Require 2 consecutive measurements within 0.5px
        if (Math.abs(w - last) < 0.5) {
          checks += 1;
        } else {
          checks = 0;
        }
        last = w;
        if (checks >= 2) {
          cb();
        } else {
          requestAnimationFrame(check);
        }
      };
      requestAnimationFrame(check);
    };
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(proceed);
    } else {
      proceed();
    }
  }

  // ====== Hover slow/fast for CSS mode (unchanged) ======
  #slowDown = debounce(() => {
    if (this.#isJsMode()) return; // ignore in JS/mobile mode
    if (this.#animation) return;

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation) return;

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from: 1,
      to: 0,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }, ANIMATION_OPTIONS.duration);

  #speedUp() {
    if (this.#isJsMode()) return; // ignore in JS/mobile mode
    this.#slowDown.cancel();

    const animation = this.refs.wrapper.getAnimations()[0];
    if (!animation || animation.playbackRate === 1) return;

    const from = this.#animation?.current ?? 0;
    this.#animation?.cancel();

    this.#animation = animateValue({
      ...ANIMATION_OPTIONS,
      from,
      to: 1,
      onUpdate: (value) => animation.updatePlaybackRate(value),
      onComplete: () => { this.#animation = null; },
    });
  }

  // ====== Speed & sizing you already had (kept) ======
  #setSpeed(value = this.#calculateSpeed()) {
    this.style.setProperty('--marquee-speed', `${value}s`);
  }

  #calculateSpeed() {
    const speedFactor = Number(this.getAttribute('data-speed-factor'));
    const marqueeWidth = this.offsetWidth;
    const speed = Math.ceil(marqueeWidth / speedFactor / 2);
    return speed;
  }

  #handleResize = debounce(() => {
    // Respect current DPR & direction
    this.#dpr = window.devicePixelRatio || 1;
    const dir = this.dataset.movementDirection;
    this.#directionSign = (dir === 'reverse') ? 1 : -1;

    const { content } = this.refs;
    const newNumberOfCopies = this.#calculateNumberOfCopies();
    const currentNumberOfCopies = content.children.length;

    if (newNumberOfCopies > currentNumberOfCopies) {
      this.#addRepeatedItems(newNumberOfCopies - currentNumberOfCopies);
    } else if (newNumberOfCopies < currentNumberOfCopies) {
      this.#removeRepeatedItems(currentNumberOfCopies - newNumberOfCopies);
    }

    this.#duplicateContent();

    this.#settleLayout(() => {
      this.#syncDesktopAnimation();

      // 🔁 Keep JS speed in sync with desktop after resize
      {
        const distPx = this.#loopWidth();
        const cssDur = parseFloat(getComputedStyle(this.refs.wrapper).getPropertyValue('--marquee-speed')) || (distPx / 120);
        this.#jsPxPerSec = distPx / cssDur;
      }

      this.#setSpeed();
      this.#restartAnimation(); // CSS path (safe no-op in JS mode)
      this.#setMode();          // switch if crossing mobile/desktop breakpoint
      this.setAttribute('data-ready', '');
    });
  }, 250);

  #restartAnimation() {
    if (this.#isJsMode()) return;
    const animations = this.refs.wrapper.getAnimations();
    requestAnimationFrame(() => {
      for (const animation of animations) {
        animation.currentTime = 0;
      }
    });
  }

  #duplicateContent() {
    this.clonedContent?.remove();
    const clone = /** @type {HTMLElement} */ (this.refs.content.cloneNode(true));
    clone.setAttribute('aria-hidden', 'true');
    clone.removeAttribute('ref');
    this.refs.wrapper.appendChild(clone);
  }

  #cleanupClonedContent() {
    this.clonedContent?.remove();
  }

  #addRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    const wrapper = content.firstElementChild;
    if (!wrapper) return;

    for (let i = 0; i < numberOfCopies - 1; i++) {
      const clone = wrapper.cloneNode(true);
      content.appendChild(clone);
    }
  }

  #removeRepeatedItems(numberOfCopies = this.#calculateNumberOfCopies()) {
    const { content } = this.refs;
    for (let i = 0; i < numberOfCopies; i++) {
      content.lastElementChild?.remove();
    }
  }

  #calculateNumberOfCopies() {
    const { content } = this.refs;
    const marqueeWidth = this.offsetWidth;
    const marqueeRepeatedItemWidth =
      content.firstElementChild instanceof HTMLElement ? content.firstElementChild.offsetWidth : 1;
    return marqueeRepeatedItemWidth === 0 ? 1 : Math.ceil(marqueeWidth / marqueeRepeatedItemWidth);
  }

  // ====== Desktop CSS animation sync (new) ======
  #syncDesktopAnimation() {
    const distPx = this.#loopWidth();
    // Use PX distance to avoid % rounding shimmer
    this.refs.wrapper.style.setProperty('--marquee-shift', `${distPx}px`);

    // Derive a sensible duration for desktop CSS animation (keeps your look)
    const pxPerSec = 120; // desktop feel; adjust globally if you like
    const duration = distPx / pxPerSec;
    this.refs.wrapper.style.setProperty('--marquee-speed', `${duration}s`);
  }

  #loopWidth() {
    // One full cycle = width of the FIRST content group + the inter-group gap
    const firstGroup = this.refs.content;
    const gapPx = this.#getGapPx(this.refs.wrapper);
    const w = firstGroup?.scrollWidth || 0;
    return w + gapPx;
  }

  #getGapPx(el) {
    const g = getComputedStyle(el).gap || '0px';
    const n = parseFloat(g);
    return Number.isFinite(n) ? n : 0;
  }

  // ====== Mode switching ======
  #isJsMode() {
    return this.hasAttribute('data-disabled'); // matches your CSS gate
  }

  #setMode() {
    const mobile = window.matchMedia('(max-width: 749px)').matches;
    // Always start each mode from a clean transform to avoid breakpoint hiccup
    this.refs.wrapper.style.transform = 'translate3d(0,0,0)';

    if (mobile) {
      this.setAttribute('data-disabled', '');   // turn OFF CSS animation
      this.#startJsLoop();                      // and run crisp JS mode
    } else {
      this.removeAttribute('data-disabled');    // enable CSS animation
      this.#stopJsLoop();
    }
  }

  // ====== Pixel-snapped JS loop (mobile) ======
  #startJsLoop() {
    this.#stopJsLoop();
    const limit = this.#loopWidth();
    this.#jsLast = performance.now();

    // Progress along the path in [0, limit)
    let p = 0;

    const tick = (now) => {
      const dt = (now - this.#jsLast) / 1000;
      this.#jsLast = now;

      // Advance progress; 'normal' = left (negative translate), 'reverse' = right
      p = (p + this.#jsPxPerSec * dt) % limit;

      // Convert progress to signed X by direction, then snap to device pixels
      const x = this.#directionSign * p;
      const snapped = Math.round(x * this.#dpr) / this.#dpr;

      this.refs.wrapper.style.transform = `translate3d(${snapped}px,0,0)`;
      this.#jsRaf = requestAnimationFrame(tick);
    };

    this.#jsRaf = requestAnimationFrame(tick);
  }

  #stopJsLoop() {
    if (this.#jsRaf) cancelAnimationFrame(this.#jsRaf);
    this.#jsRaf = null;
  }
}

// Define the animateValue function (unchanged)
function animateValue({ from, to, duration, onUpdate, easing = (t) => t * t * (3 - 2 * t), onComplete }) {
  const startTime = performance.now();
  let cancelled = false;
  let currentValue = from;

  function animate(currentTime) {
    if (cancelled) return;

    const elapsed = currentTime - startTime;
    const progress = Math.min(elapsed / duration, 1);
    const easedProgress = easing(progress);
    currentValue = from + (to - from) * easedProgress;

    onUpdate(currentValue);

    if (progress < 1) {
      requestAnimationFrame(animate);
    } else if (typeof onComplete === 'function') {
      onComplete();
    }
  }

  requestAnimationFrame(animate);

  return {
    get current() {
      return currentValue;
    },
    cancel() {
      cancelled = true;
    },
  };
}

if (!customElements.get('marquee-component')) {
  customElements.define('marquee-component', MarqueeComponent);
}

// 📌 Locked Marquee Speed on Page Load (updated to use public recompute)
window.addEventListener('load', () => {
  const marquee = document.querySelector('marquee-component');
  if (!marquee) return;

  // Only set the speed once if not already locked
  if (!marquee.hasAttribute('data-speed-locked')) {
    const factor = window.innerWidth <= 767 ? 20 : 40; // tweak as you like
    marquee.setAttribute('data-speed-factor', factor);
    marquee.setAttribute('data-speed-locked', 'true');

    // Recompute without re-calling connectedCallback
    if (typeof marquee.recomputeAll === 'function') {
      marquee.recomputeAll();
    }
  }
});
